import React, { useEffect, useState } from 'react';
import SmallButton from '../../../components/SmallButton';
import { axiosKK } from '../../../services/networkRequest';
import {
  WarehouseForecastCell,
  WarehouseForecastResponse,
} from '../../../models/APIModels';
import errorMessage from '../../../services/errorMessage';
import { downloadCsv } from '../../../services/downloadBlob';
import { ArrowDownTrayIcon } from '@heroicons/react/24/solid';

interface ForecastDetailOrder {
  orderId: number;
  customer?: string;
  postcode?: string;
  boxes?: number;
}
interface ForecastDetailResponse {
  date: string;
  type: 'inbound' | 'outbound';
  orders: ForecastDetailOrder[];
}

export default function ForecastTab() {
  const [from, setFrom] = useState<string>('');
  const [to, setTo] = useState<string>('');
  const [cells, setCells] = useState<WarehouseForecastCell[]>([]);
  const [loading, setLoading] = useState(true);
  const [netError, setNetError] = useState<string>();

  const [drawer, setDrawer] = useState<ForecastDetailResponse | null>(null);
  const [drawerLoading, setDrawerLoading] = useState(false);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setNetError(undefined);
    const params: any = {};
    if (from) params.from = from;
    if (to) params.to = to;
    axiosKK
      .get<WarehouseForecastResponse>('/logistics/dashboard/warehouse/forecast', { params })
      .then(({ data }) => {
        if (!cancelled) setCells(data.cells || []);
      })
      .catch((err) => {
        if (!cancelled) errorMessage(setNetError)(err);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [from, to]);

  const openDetails = (date: string, type: 'inbound' | 'outbound') => {
    setDrawer({ date, type, orders: [] });
    setDrawerLoading(true);
    axiosKK
      .get<ForecastDetailResponse>(
        `/logistics/dashboard/warehouse/forecast/${date}/details`,
        { params: { type } }
      )
      .then(({ data }) => setDrawer(data))
      .catch((err) => errorMessage(setNetError)(err))
      .finally(() => setDrawerLoading(false));
  };

  return (
    <>
      {netError && <p className="error">{netError}</p>}

      <section className="admin-warehouse-forecast">
        <div className="logistics-controls-container">
          <div className="filters-group">
            <div className="filter-item">
              <label>From</label>
              <input type="date" value={from} onChange={(e) => setFrom(e.target.value)} />
            </div>
            <div className="filter-item">
              <label>To</label>
              <input type="date" value={to} onChange={(e) => setTo(e.target.value)} />
            </div>
            <div className="filter-item filter-item--action">
              <SmallButton
                onClick={() => {
                  const params: Record<string, unknown> = {};
                  if (from) params.from = from;
                  if (to) params.to = to;
                  downloadCsv(
                    '/logistics/dashboard/warehouse/forecast',
                    `warehouse-forecast-${from || 'all'}.csv`,
                    params
                  ).catch((err) => errorMessage(setNetError)(err));
                }}
              >
                <ArrowDownTrayIcon width={16} height={16} /> Export CSV
              </SmallButton>
            </div>
          </div>
        </div>

        {loading ? (
          <p>Loading forecast…</p>
        ) : cells.length === 0 ? (
          <p>No forecast data.</p>
        ) : (
          <table className="pricing-table forecast-grid">
            <thead>
              <tr>
                <th>Date</th>
                <th>Day</th>
                <th>Inbound Orders</th>
                <th>Inbound Boxes</th>
                <th>Outbound Orders</th>
                <th>Outbound Boxes</th>
              </tr>
            </thead>
            <tbody>
              {cells.map((c) => (
                <tr key={c.date}>
                  <td>{c.displayDate}</td>
                  <td>{c.dayName}</td>
                  <td
                    className="forecast-grid__cell forecast-grid__cell--click"
                    onClick={() => openDetails(c.date, 'inbound')}
                  >
                    {c.inboundOrders}
                  </td>
                  <td>{c.inboundBoxes}</td>
                  <td
                    className="forecast-grid__cell forecast-grid__cell--click"
                    onClick={() => openDetails(c.date, 'outbound')}
                  >
                    {c.outboundOrders}
                  </td>
                  <td>{c.outboundBoxes}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </section>

      {drawer && (
        <div className="logistics-modal__overlay" onClick={() => setDrawer(null)}>
          <div className="logistics-modal__container" onClick={(e) => e.stopPropagation()}>
            <div className="logistics-modal__header">
              <div className="logistics-modal__header-info">
                <h3>
                  {drawer.type === 'inbound' ? 'Inbound' : 'Outbound'} · {drawer.date}
                </h3>
              </div>
              <button
                type="button"
                className="logistics-modal__close"
                onClick={() => setDrawer(null)}
              >
                &times;
              </button>
            </div>
            <div className="logistics-modal__body">
              {drawerLoading ? (
                <p>Loading…</p>
              ) : drawer.orders.length === 0 ? (
                <p>No orders.</p>
              ) : (
                <table className="pricing-table">
                  <thead>
                    <tr>
                      <th>Order</th>
                      <th>Customer</th>
                      <th>Postcode</th>
                      <th>Boxes</th>
                    </tr>
                  </thead>
                  <tbody>
                    {drawer.orders.map((o) => (
                      <tr key={o.orderId}>
                        <td>#{o.orderId}</td>
                        <td>{o.customer || '—'}</td>
                        <td>{o.postcode || '—'}</td>
                        <td>{o.boxes ?? '—'}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>
          </div>
        </div>
      )}
    </>
  );
}
